import { useAuth } from 'hooks'
import { useVeraSpaceStore } from 'stores'
import { Permissions, RoleType } from 'types'

interface UseCanAccessParams {
  action?: Permissions
  role?: RoleType
}

interface UseCanAccessResult {
  isHasAccess: boolean
}

export function useCanAccess(params: UseCanAccessParams): UseCanAccessResult {
  const { profile } = useAuth()
  const { action, role } = params

  // VERA-876: when Vera staff are in a company's space their effective permissions
  // are REPLACED by the company's vera_support grant — not intersected. Role checks
  // are not meaningful inside a space context (the staffer has no company role), so
  // role-based checks always return false while in space mode.
  const spaceCompanyId = useVeraSpaceStore((s) => s.companyId)
  const spacePermissions = useVeraSpaceStore((s) => s.permissions)

  const hasPermission = () => {
    if (!profile) return false

    // In space mode: role checks are disabled; action checks use the space grant.
    if (spaceCompanyId) {
      if (role) return false
      if (!action) return false
      return spacePermissions.includes(action)
    }

    // Normal mode: role or permission check against the user's own profile.
    if (role) {
      return profile?.role.name === role
    }

    if (!action) return false

    return profile?.role.permissions.some(permission => permission.name === action)
  }

  return {
    isHasAccess: hasPermission(),
  }
}
